import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  DatePicker,
  message,
  Card,
  Statistic,
  Popconfirm,
  Spin,
  Pagination,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExpenseTransactionModalMobile } from '../../components/expenses/ExpenseTransactionModalMobile';
import {
  ExpenseTransaction,
  ExpenseCategory,
  getExpenseTransactions,
  getExpenseCategories,
  deleteExpenseTransaction,
  formatCurrency,
} from '../../services/expenseService';
import styles from './ExpensesPageMobile.module.css';

const { RangePicker } = DatePicker;

export const ExpensesPageMobile: React.FC = () => {
  const [transactions, setTransactions] = useState<ExpenseTransaction[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<ExpenseTransaction | undefined>();
  const [filters, setFilters] = useState({
    category_id: undefined as number | undefined,
    start_date: undefined as dayjs.Dayjs | undefined,
    end_date: undefined as dayjs.Dayjs | undefined,
    created_by: undefined as number | undefined,
    search: '',
  });

  const loadTransactions = async () => {
    setLoading(true);
    try {
      // Chuẩn bị params cho API
      const apiParams: any = {
        page: currentPage,
        limit: pageSize,
        category_id: filters.category_id,
        created_by: filters.created_by,
        search: filters.search,
      };

      // Xử lý date range
      if (filters.start_date) {
        apiParams.start_date = filters.start_date.format('YYYY-MM-DD');
      }
      if (filters.end_date) {
        apiParams.end_date = filters.end_date.format('YYYY-MM-DD');
      }

      const response = await getExpenseTransactions(apiParams);
      setTransactions(response.data);
      setTotal(response.pagination.total);
    } catch (error) {
      message.error('Không thể tải danh sách giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error) {
      message.error('Không thể tải danh mục');
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddTransaction = () => {
    setEditingTransaction(undefined);
    setIsModalOpen(true);
  };

  const handleEditTransaction = (record: ExpenseTransaction) => {
    setEditingTransaction(record);
    setIsModalOpen(true);
  };

  const handleDeleteTransaction = async (id: number) => {
    try {
      await deleteExpenseTransaction(id);
      message.success('Xóa giao dịch thành công');
      loadTransactions();
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa giao dịch');
    }
  };

  const handleModalSuccess = () => {
    loadTransactions();
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value 
    }));
    setCurrentPage(1);
  };

  const getCategoryById = (id: number) => {
    return categories.find(c => c.id === id);
  };

  const totalAmount = transactions.reduce((sum, transaction) => sum + transaction.amount, 0);

  const renderTransactionCard = (transaction: ExpenseTransaction) => {
    const category = getCategoryById(transaction.category_id);
    
    return (
      <div key={transaction.id} className={styles.transactionCard}>
        <div className={styles.transactionHeader}>
          <div className={styles.transactionDate}>
            {dayjs(transaction.expense_date).format('DD/MM/YYYY')}
          </div>
          <div className={styles.transactionActions}>
            <Button
              className={`${styles.actionButton} ${styles.editButton}`}
              icon={<EditOutlined />}
              onClick={() => handleEditTransaction(transaction)}
            >
            </Button>
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa giao dịch này?"
              onConfirm={() => handleDeleteTransaction(transaction.id)}
              okText="Có"
              cancelText="Không"
            >
              <Button
                className={`${styles.actionButton} ${styles.deleteButton}`}
                icon={<DeleteOutlined />}
              >
              </Button>
            </Popconfirm>
          </div>
        </div>

        <div className={styles.transactionContent}>
          <div
            className={styles.categoryIcon}
           // style={{ backgroundColor: category?.color || '#666' }}
          >
            {category?.icon || '💰'}
          </div>
          
          <div className={styles.transactionInfo}>
            <div className={styles.transactionAmount}>
              {formatCurrency(transaction.amount)}
            </div>
            <div className={styles.transactionCategory}>
              {category?.name || 'Không xác định'}
            </div>
            {transaction.notes && (
              <div className={styles.transactionNotes}>
                {transaction.notes}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <WalletOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có giao dịch chi tiêu nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm giao dịch đầu tiên của bạn
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      {/* Header với thống kê */}
      <div className={styles.header}>
        
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {formatCurrency(totalAmount)}
            </div>
            <div className={styles.statLabel}>
              Tổng chi tiêu
            </div>
          </div>
          
          <div className={`${styles.statCard} ${styles.statCardSecondary}`}>
            <div className={styles.statValue}>
              {total}
            </div>
            <div className={styles.statLabel}>
              Giao dịch
            </div>
          </div>
        </div>
      </div>

      {/* Bộ lọc */}
      <div className={styles.filtersSection}>
        <div className={styles.filterRow}>
          <Select
            className={`${styles.filterInput} ${styles.categorySelect}`}
            placeholder="Danh mục"
            allowClear
            onChange={(value) => handleFilterChange('category_id', value)}
          >
            {categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.icon} {category.name}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className={styles.filterRow}>
          <div className={styles.dateInputsContainer}>
            <DatePicker
              className={`${styles.filterInput} ${styles.datePicker}`}
              placeholder="Từ ngày"
              onChange={(date) => handleFilterChange('start_date', date)}
            />
            <DatePicker
              className={`${styles.filterInput} ${styles.datePicker}`}
              placeholder="Đến ngày"
              onChange={(date) => handleFilterChange('end_date', date)}
            />
          </div>
        </div>

        <div className={styles.filterRow}>
          <Input
            className={`${styles.filterInput} ${styles.searchInput}`}
            placeholder="Tìm kiếm..."
            prefix={<SearchOutlined />}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>

        <div className={styles.filterRow}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className={`${styles.filterInput} ${styles.addButton}`}
            onClick={handleAddTransaction}
          >
            Thêm giao dịch
          </Button>
        </div>
      </div>

      {/* Danh sách giao dịch */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      ) : transactions.length > 0 ? (
        <>
          <div className={styles.transactionsList}>
            {transactions.map(renderTransactionCard)}
          </div>
          
          <div className={styles.pagination}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} / ${total}`
              }
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              }}
            />
          </div>
        </>
      ) : (
        renderEmptyState()
      )}



      {/* Modal thêm/sửa giao dịch */}
      <ExpenseTransactionModalMobile
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={editingTransaction}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
